import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { Toaster } from '@/components/ui/sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let isRefreshing = false;
let refreshQueue = [];

function processQueue(error, token = null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
}

axios.interceptors.response.use(null, async (error) => {
  const config = error.config;

  if (error.response?.status === 429 && !config._retryCount) {
    config._retryCount = 0;
  }
  if (error.response?.status === 429 && config._retryCount < 2) {
    config._retryCount++;
    const retryAfter = error.response.headers['retry-after'];
    const delay = retryAfter ? Math.min(parseInt(retryAfter, 10) * 1000, 5000) : 2000;
    await new Promise(r => setTimeout(r, delay));
    return axios(config);
  }

  const detail = error.response?.data?.detail;
  if (error.response?.status === 401 && detail === "Token expired" && !config._isRetryAfterRefresh) {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('cachedCategories');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = '/';
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      }).then(token => {
        config.headers['Authorization'] = `Bearer ${token}`;
        config._isRetryAfterRefresh = true;
        return axios(config);
      });
    }

    isRefreshing = true;
    try {
      const response = await axios.post(`${API}/refresh`, { refresh_token: refreshToken });
      const newToken = response.data.token;
      const newRefreshToken = response.data.refresh_token;
      localStorage.setItem('adminToken', newToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      processQueue(null, newToken);
      config.headers['Authorization'] = `Bearer ${newToken}`;
      config._isRetryAfterRefresh = true;
      return axios(config);
    } catch (refreshError) {
      processQueue(refreshError);
      localStorage.removeItem('adminToken');
      localStorage.removeItem('cachedCategories');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = '/';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }

  return Promise.reject(error);
});

// Disable browser's automatic scroll restoration to prevent scroll position issues
if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
    <Toaster position="top-right" />
  </React.StrictMode>
);